import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button, Divider, Grid, InputLabel, Stack } from '@mui/material';

import { KaseApi } from '@/api';
import {
  FormRoot,
  FormSelectField,
  RenderFormField,
  ViewItem,
} from '@/components';
import { useKaseContext } from '@/hooks';
import { IArrestCharge, IArrestChargeForm, IPenalCode } from '@/models';
import { useToastStore } from '@/store';
import { colors } from '@/theme/variables';
import { getPenalCodeLabel } from '@/utils';

import { useKaseArrestsContext } from '../KaseArrestsContext';
import { AttemptIndicatorSelectItems } from './../../data';

const ArrestChargeInfoForm = () => {
  const { updateToast } = useToastStore();
  const {
    arrestDrawer,
    setArrestDrawer,
    currentArrestee,
    currentArrestCharge,
    setCurrentArrestee,
  } = useKaseArrestsContext();
  const { kase } = useKaseContext();
  const methods = useForm<IArrestChargeForm>({
    defaultValues: {},
    mode: 'all',
  });
  const [penalCodes, setPenalCodes] = useState<IPenalCode[] | undefined>([]);
  const [selectedPenalCode, setSelectedPenalCode] = useState<
    IPenalCode | undefined
  >();
  const {
    handleSubmit,
    getValues,
    watch,
    formState: { isValid },
    reset,
  } = methods;
  const attemptIndicatorValue = watch('attemptIndicator');

  useEffect(() => {
    fetchOffenseList();
  }, []);

  const fetchOffenseList = async () => {
    try {
      const { data } = await KaseApi.getOffenses(kase?._id as string, {
        limit: 1000,
      });

      const assignedPenalCodeIds = new Set(
        currentArrestee?.arrestCharges?.map((charge) => charge?.penalCode?._id),
      );

      const currPenalCodes = data.results.reduce<IPenalCode[]>(
        (acc, offense) => {
          if (
            offense?.penalCode &&
            !assignedPenalCodeIds.has(offense?.penalCode?._id)
          ) {
            acc.push(offense?.penalCode);
          }
          return acc;
        },
        [],
      );
      setPenalCodes(currPenalCodes);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };
  useEffect(() => {
    if (arrestDrawer === 'charge') {
      setSelectedPenalCode(currentArrestCharge?.penalCode);
      reset({
        attemptIndicator: currentArrestCharge?.attemptIndicator,
      });
    }
  }, [arrestDrawer]);

  const handleChangeSelectedPenalCode = (
    _name: string,
    id: string | number,
  ) => {
    const existingArrestCharge = (currentArrestee?.arrestCharges || []).find(
      (item) => item?.penalCode?._id === id,
    );
    if (existingArrestCharge) {
      updateToast({
        open: true,
        message: 'This penal code was already charged.',
      });
    } else {
      setSelectedPenalCode(
        penalCodes?.find((penalCode) => penalCode._id === id),
      );
    }
  };

  const onSubmit = async () => {
    if (!currentArrestee || !selectedPenalCode) {
      return;
    }
    const values = getValues();
    const arrestCharges: IArrestCharge[] = currentArrestee.arrestCharges || [];

    const newArrestCharge: IArrestCharge = {
      ...values,
      penalCode: selectedPenalCode,
    };

    if (currentArrestCharge) {
      const index = arrestCharges.findIndex(
        ({ penalCode }) =>
          penalCode?._id === currentArrestCharge?.penalCode?._id,
      );
      arrestCharges[index] = newArrestCharge;
    } else {
      arrestCharges.push(newArrestCharge);
    }

    setCurrentArrestee({
      ...currentArrestee,
      arrestCharges,
    });
    setArrestDrawer(null);
  };

  return (
    <FormProvider {...methods}>
      <FormRoot
        onSubmit={handleSubmit(onSubmit)}
        sx={{ height: '100vh', pb: 0 }}
      >
        <Grid container spacing={2}>
          <Grid xs={12} item>
            <InputLabel>Penal code</InputLabel>
            <FormSelectField
              handleChange={handleChangeSelectedPenalCode}
              name="penalCode"
              items={penalCodes?.map((penalCode) => {
                return {
                  label: getPenalCodeLabel(penalCode),
                  value: penalCode?._id ?? '',
                };
              })}
              type="select"
              clearable
            />
          </Grid>
          {selectedPenalCode && (
            <>
              <Grid xs={12} item>
                <ViewItem
                  sx={{
                    mt: -2,
                    color: colors.grey[80],
                    flexDirection: 'column',
                  }}
                  value={getPenalCodeLabel(
                    selectedPenalCode,
                    attemptIndicatorValue,
                  )}
                  md={12}
                  label="Penal code"
                />
              </Grid>
              <Grid xs={12} item>
                <ViewItem
                  sx={{
                    mt: -4,
                    color: colors.grey[80],
                    flexDirection: 'column',
                  }}
                  value={selectedPenalCode.lawDescription}
                  md={12}
                  label="Law description"
                />
              </Grid>
              <Divider sx={{ my: 1, width: '100%' }} />
            </>
          )}
          <Grid xs={12} item>
            <RenderFormField
              label="Attempt indicator"
              type="select"
              name="attemptIndicator"
              items={AttemptIndicatorSelectItems}
            />
          </Grid>
        </Grid>
        <Stack flexDirection="column" width="100%" marginTop="auto" padding="0">
          <>
            <Divider sx={{ marginBottom: '15px', width: '100%' }} />
            <Stack flexDirection="row" justifyContent="flex-end">
              <Button
                variant="contained"
                color="inherit"
                size="medium"
                sx={{ mr: 2 }}
                onClick={() => setArrestDrawer(null)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                size="medium"
                disabled={!selectedPenalCode || !isValid}
                onClick={handleSubmit(onSubmit)}
              >
                Save
              </Button>
            </Stack>
          </>
        </Stack>
      </FormRoot>
    </FormProvider>
  );
};

export default ArrestChargeInfoForm;
